import { useRouter } from 'expo-router';
import React, { useCallback } from 'react';
import { FlatList, Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { isSupabaseConfigured } from '@/config';
import { useCompetitions } from '@/features/leagues/hooks';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import type { Tables } from '@/lib/supabase/types';

const CompetitionCard = React.memo(function CompetitionCard({
  competition,
}: {
  competition: Tables<'competitions'>;
}) {
  const router = useRouter();
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();
  // Memoised so a parent re-render (locale change, query refetch) doesn't hand
  // the Pressable a fresh handler mid-gesture.
  const open = useCallback(() => {
    router.push({
      pathname: '/competition/[id]',
      params: { id: competition.id, name: competition.name },
    });
  }, [router, competition.id, competition.name]);
  return (
    <Pressable
      accessibilityRole="button"
      onPress={open}
      // The active: feedback used to sit on the inner Card, which never receives
      // the press, so a tap looked dead and users re-tapped. Drive the feedback
      // (Android ripple + dim) from the Pressable that actually handles the tap.
      android_ripple={{ color: 'rgba(255,255,255,0.07)', borderless: false }}
      className="active:opacity-90"
    >
      {/* No `active:` on this inner Card: NativeWind turns it into a nested
          pressability that steals the touch from the parent Pressable, so taps
          off the (handler-free) logo died until you tried 3-4 times. Feedback
          lives on the Pressable above. */}
      <Card className="mx-4 mb-3 flex-row items-center gap-3 px-4 py-3.5">
        {/* Light tile behind the crest so DARK logos (CL, Egypt...) stay visible.
            Inline color: the custom Tailwind palette has no `white` utility. */}
        <View
          className="items-center justify-center rounded-xl"
          style={{ width: 46, height: 46, padding: 6, backgroundColor: '#EDF1F7' }}
        >
          <TeamCrest uri={competition.logo_url} name={competition.name} size="md" />
        </View>
        <View className="flex-1 gap-0.5">
          <AppText variant="body" weight="semibold" className="text-ink" numberOfLines={1}>
            {competition.name}
          </AppText>
          <AppText variant="micro" numberOfLines={1}>
            {competition.country ?? ''}
            {competition.current_season
              ? `  ·  ${localizeDigits(String(competition.current_season), arabicNumerals)}`
              : ''}
          </AppText>
        </View>
        {competition.is_fantasy_enabled ? (
          <View className="rounded-full bg-volt/15 px-2.5 py-1">
            <AppText variant="micro" weight="bold" className="text-volt">
              {t('leagues.fantasyAvailable')}
            </AppText>
          </View>
        ) : null}
      </Card>
    </Pressable>
  );
});

export default function LeaguesScreen() {
  const { t } = useTranslation();
  const competitionsQuery = useCompetitions();
  const renderItem = useCallback(
    ({ item }: { item: Tables<'competitions'> }) => <CompetitionCard competition={item} />,
    [],
  );

  return (
    <Screen>
      <View className="px-4 pb-3 pt-2">
        <AppText variant="title">{t('leagues.title')}</AppText>
      </View>

      {!isSupabaseConfigured ? (
        <EmptyState
          icon="settings-outline"
          title={t('common.notConfiguredTitle')}
          body={t('common.notConfiguredBody')}
        />
      ) : competitionsQuery.isPending ? (
        <View className="gap-3 px-4">
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} className="h-20 w-full" />
          ))}
        </View>
      ) : competitionsQuery.isError ? (
        <ErrorState
          error={competitionsQuery.error}
          onRetry={() => competitionsQuery.refetch()}
        />
      ) : (competitionsQuery.data ?? []).length === 0 ? (
        <EmptyState
          icon="trophy-outline"
          title={t('leagues.noCompetitionsTitle')}
          body={t('leagues.noCompetitionsBody')}
        />
      ) : (
        <FlatList
          data={competitionsQuery.data ?? []}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
          // removeClippedSubviews (Android default) clips the row's touch target
          // as it scrolls, which dropped the first one or two taps - the cause of
          // "I have to tap the league 3 times". Off = every tap registers.
          removeClippedSubviews={false}
          initialNumToRender={12}
          contentContainerStyle={{ paddingBottom: 24, paddingTop: 4 }}
        />
      )}
    </Screen>
  );
}
